import { ControlledTypeAhead } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';
import { CustomTooltip } from '@AMIEWEB/Common';
import { selectedExperienceSet } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import { IGroupedTypeAheadOption } from '@AMIEWEB/NewOrder/types';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import UnitCollapsible from '@AMIEWEB/Unit/Common/UnitCollapsible';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Grid } from '@mui/material';
import { TravelExperienceSections } from 'app/enums/Common';
import { getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isRequiredTravelExperience } from '../../helper';

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
  },
  // Modal Body Styles
  checkbox: {
    '& label': {
      '& .MuiButtonBase-root.MuiCheckbox-root.Mui-checked': {
        color: `${theme.palette.system.main} !important`,
      },
      '&:hover .MuiButtonBase-root.MuiCheckbox-root': {
        color: `${theme.palette.system.main} !important`,
      },
    },
  },
  travelExperienceWrapper: {
    display: 'grid',
    gap: '12px',
  },
  actionButtons: {
    color: `${theme.palette.system.neutralGray}`,
    borderRadius: '20px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${theme.palette.system.skyBlue}`,
      color: `${theme.palette.system.main}`,
    },
  },
  labelMarginRight: {
    marginRight: '11px',
  },
}));

export const TravelExperienceTile = props => {
  const { skillSetIndex, groupIndex, value: skillSetValue } = props;
  const { setValue, watch } = useFormContext();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const experienceSet = useSelector(selectedExperienceSet);

  const { t } = useTranslation();
  const [dataList, setDataList] = useState<IGroupedTypeAheadOption[]>([]);
  const [showInputs, setShowInputs] = useState<boolean>(false);
  const [recordAdded, setRecordAdded] = useState<boolean>(false);

  const travelExperiences = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience`);

  const getTravelExperienceDropDownOptions = async () => {
    const options = await getSearchFilterOptions('skillsetExperienceType');
    setDataList([
      ...getDropdownOptionSection(TravelExperienceSections.NoOfAssignments, options, 1),
      ...getDropdownOptionSection(TravelExperienceSections.YearsOfExperience, options, 2),
    ]);
  };

  const getDropdownOptionSection = (
    sectionTitle: TravelExperienceSections,
    options: any,
    sectionId: number,
  ): IGroupedTypeAheadOption[] => {
    return options.map(x => {
      // Handle cases where x.value might be undefined
      const optionValue = x.value ?? x.id ?? x.optionId ?? '';
      return {
        groupBy: sectionTitle,
        label: x.name || x.label || '',
        object: {
          optionId: optionValue,
          title: x.name || x.label || '',
          id: `${sectionId}-${optionValue}`,
        },
      };
    });
  };

  const remove = () => {
    setShowInputs(false);
    setRecordAdded(false);
    if (experienceSet.groups[groupIndex]?.skillsets[skillSetIndex]?.travelExperience) {
      dispatch(orderCreationNewActions.resetTravelExperience({ groupIndex, skillSetIndex }));
    }
  };

  useEffect(() => {
    getTravelExperienceDropDownOptions();
  }, []);

  useEffect(() => {
    if (skillSetValue?.travelExperience && !recordAdded) {
      const travel = skillSetValue?.travelExperience;
      
      if (travel?.experienceType) {
        setValue(
          `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceType`,
          travel.experienceType,
        );
        addTravelExperience();
        return;
      }
      
      if (travel?.experienceTypeText && travel?.experienceTypeId) {
        let sectionId = 2,
          sectionName = TravelExperienceSections.YearsOfExperience;
        if (skillSetValue?.travelExperience?.numberOfAssignments > 0) {
          sectionId = 1;
          sectionName = TravelExperienceSections.NoOfAssignments;
        }

        const optionSelected = {
          groupBy: sectionName,
          label: travel.experienceTypeText,
          object: {
            optionId: travel.experienceTypeId,
            title: travel.experienceTypeText,
            id: `${sectionId}-${travel.experienceTypeId}`,
          },
        };
        setValue(
          `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceType`,
          optionSelected,
        );
        addTravelExperience();
      }
    }
  }, [skillSetValue?.travelExperience, recordAdded, groupIndex, skillSetIndex, setValue]);

  const addTravelExperience = () => {
    if (!recordAdded) {
      setShowInputs(true);
      setRecordAdded(true);
    }
  };

  const updateIsAccordianExpanded = (value: boolean) => {
    //experienceType null means there is no record added
    if (!value && !travelExperiences?.experienceType) {
      setRecordAdded(false);
    }
  };

  return (
    <Grid item xs={12}>
      <UnitCollapsible
        title={t('order.createUnit.travelExperience.sectionTitle', {
          travelExperienceCount: recordAdded ? 1 : 0,
        })}
        iconToolTipContent={t('order.createUnit.travelExperience.addTravelExperience')}
        onClickHandler={addTravelExperience}
        fieldCount={recordAdded ? 1 : 0}
        isDisabled={recordAdded}
        isCollapsible={skillSetValue.length > 0}
        updateIsExpanded={updateIsAccordianExpanded}
        showIcon={false}
        enableSummaryBackground
        children={
          <Grid container>
            <Grid item xs={12} className={classes.travelExperienceWrapper}>
              {showInputs && (
                <Grid>
                  <TravelExperienceInfo
                    dataList={dataList}
                    remove={remove}
                    skillSetIndex={skillSetIndex}
                    groupIndex={groupIndex}
                    skillSetValue={skillSetValue}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        }
      />
    </Grid>
  );
};
const TravelExperienceInfo = ({ dataList, remove, skillSetIndex, groupIndex, skillSetValue }) => {
  const { control, register, watch, setValue } = useFormContext();
  register(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceTypeId`);
  register(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceTypeText`);

  const experienceTypeValue = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceType`,
  );
  const numberOfAssignments = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.numberOfAssignments`,
  );
  const yearsOfExperience = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.yearsOfExperience`,
  );
  const monthsOfExperience = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.monthsOfExperience`,
  );

  useEffect(() => {
    setValue(
      `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.isRequired`,
      isRequiredTravelExperience({
        experienceTypeValue,
        numberOfAssignments,
        yearsOfExperience,
        monthsOfExperience,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceTypeValue, groupIndex, monthsOfExperience, numberOfAssignments, skillSetIndex, yearsOfExperience]);

  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Grid container xs={12}>
      <Grid container gap={2} xs={11.63} mt={1}>
        <Grid item xs={2.18} marginRight={Boolean(experienceTypeValue?.groupBy) ? 0 : '11px'}>
          <ControlledTypeAhead
            register={register}
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceType`}
            label={t('order.createUnit.travelExperience.title')}
            control={control}
            options={dataList || []}
            customScroll={true}
            onChange={value => {
              setValue(
                `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceTypeId`,
                value?.object.optionId,
              );
              setValue(
                `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.experienceTypeText`,
                value?.label,
              );
            }}
            disabled={false}
            id={'orderDetailsTravelExperience'}
            showDropdownIcon={true}
            defaultValue={skillSetValue?.travelExperience?.experienceType}
            groupBy={'groupBy'}
          />
        </Grid>
        {experienceTypeValue?.groupBy === TravelExperienceSections.NoOfAssignments && (
          <Grid item xs={1.88} marginRight={'11px'}>
            <Controller
              control={control}
              name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.numberOfAssignments`}
              defaultValue={skillSetValue?.travelExperience?.numberOfAssignments ?? null}
              render={({ field }) => (
                <SingleLineInput
                  id={'numberOfAssignments'}
                  inputDefaultValue={null}
                  inputLabel={t('order.createUnit.travelExperience.numberOfAssignments')}
                  inputName={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.numberOfAssignments`}
                  inputValue={field.value ?? null}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const assignments = parseInt(event.target.value, 10);
                    if (isNaN(assignments)) {
                      field.onChange(null);
                    }
                    if (0 <= assignments && assignments < 11) {
                      field.onChange(assignments);
                    }
                  }}
                  shrinkLabel={true}
                  inputType={'number'}
                  fieldStyle={classes.fieldStyle}
                />
              )}
            />
          </Grid>
        )}
        {experienceTypeValue?.groupBy === TravelExperienceSections.YearsOfExperience && (
          <Grid item xs={1.15}>
            <Controller
              name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.yearsOfExperience`}
              control={control}
              defaultValue={skillSetValue?.travelExperience?.yearsOfExperience ?? null}
              render={({ field }) => (
                <SingleLineInput
                  id={'orderExperienceInYears'}
                  inputDefaultValue={null}
                  inputLabel={t('facility.units.unitDetails.unitRequirements.experienceIn')}
                  inputName={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.yearsOfExperience`}
                  inputValue={field.value ?? null}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const years = parseInt(event.target.value);
                    if (isNaN(years) || years < 0) {
                      field.onChange(null);
                    }
                    if (years >= 0) {
                      field.onChange(years);
                    }
                  }}
                  showEndAdornment={true}
                  shrinkLabel={true}
                  endAdornmentValue={t('facility.units.unitDetails.unitRequirements.years')}
                  inputType={'number'}
                  fieldStyle={classes.fieldStyle}
                />
              )}
            />
          </Grid>
        )}
        {experienceTypeValue?.groupBy === TravelExperienceSections.YearsOfExperience && (
          <Grid item xs={1.15} marginRight={'11px'}>
            <Controller
              name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.monthsOfExperience`}
              control={control}
              defaultValue={skillSetValue?.travelExperience?.monthsOfExperience ?? null}
              render={({ field }) => (
                <SingleLineInput
                  id={'orderExperienceInMonths'}
                  inputDefaultValue={null}
                  inputLabel={t('facility.units.unitDetails.unitRequirements.experienceIn')}
                  inputName={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.monthsOfExperience`}
                  inputValue={field.value ?? null}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const months = parseInt(event.target.value, 10);
                    if (isNaN(months)) {
                      field.onChange(null);
                    }
                    if (0 <= months && months < 12) {
                      field.onChange(months);
                    }
                  }}
                  showEndAdornment={true}
                  shrinkLabel={true}
                  endAdornmentValue={t('facility.units.unitDetails.unitRequirements.months')}
                  inputType={'number'}
                  fieldStyle={classes.fieldStyle}
                />
              )}
            />
          </Grid>
        )}
        <Grid item sx={{ alignSelf: 'center' }}>
          <Controller
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.isRequired`}
            control={control}
            defaultValue={skillSetValue?.travelExperience?.isRequired}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={isRequiredTravelExperience({
                  experienceTypeValue,
                  numberOfAssignments,
                  yearsOfExperience,
                  monthsOfExperience,
                })}
                handleChecked={event => { }}
                checkboxLabel={t('facility.units.unitDetails.unitRequirements.required')}
                checkboxName={'experienceTravelExperiencesRequired'}
                labelStyle={classes.labelMarginRight}
                id={'experienceTravelExperiencesRequired'}
                isdisabled
              />
            )}
          />
        </Grid>
        <Grid item sx={{ alignSelf: 'center' }} className={`${experienceTypeValue ? classes.checkbox : ''}`}>
          <Controller
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.withinTwoYears`}
            defaultValue={skillSetValue?.travelExperience?.withinTwoYears}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('order.createUnit.travelExperience.within2Years')}
                checkboxName={'experienceTravelExperiencesWithin2Years'}
                labelStyle={classes.labelMarginRight}
                id={'experienceTravelExperiencesWithin2Years'}
                isdisabled={!Boolean(experienceTypeValue)}
              />
            )}
          />
        </Grid>
        <Grid item sx={{ alignSelf: 'center' }} className={`${experienceTypeValue ? classes.checkbox : ''}`}>
          <Controller
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].travelExperience.isAMNTravel`}
            defaultValue={skillSetValue?.travelExperience?.isAMNTravel}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('order.createUnit.travelExperience.amnTravel')}
                checkboxName={'experienceTravelExperiencesAMNTravel'}
                labelStyle={classes.labelMarginRight}
                id={'experienceTravelExperiencesAMNTravel'}
                isdisabled={!Boolean(experienceTypeValue)}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item gap={2} sx={{ alignSelf: 'center', paddingTop: 0.5 }} xs={0.37}>
        <CustomTooltip standardMargin tooltipContent={t('order.createUnit.travelExperience.deleteTravelExperience')}>
          <DeleteOutlinedIcon
            className={classes.actionButtons}
            onClick={() => {
              remove();
            }}
          />
        </CustomTooltip>
      </Grid>
    </Grid>
  );
};
